/******************************************************************************
 * ToolActionCard: reusable super-admin "tool" card. Renders a title, a short
 * notice, a single text input (typically an entity id) and a primary button
 * that runs an async operation, reporting success/error via toasts.
 *****************************************************************************/

/* Imports */
import { ReactNode, useState } from "react";
import { Button, Card, CardBody, Input, Typography } from "@material-tailwind/react";
import toast from "react-hot-toast";
/***/

/* Services */
import Loading from "../../../../../components/loading/Loading";
import { showApiErrorToast } from "../../../../../services/api/error";
/***/

/**
* ToolActionCard: a single-value admin action
* @param icon - optional leading icon
* @param title - card title
* @param notice - explanatory text
* @param inputLabel - label of the default text input (ignored when renderInput is set)
* @param buttonLabel - label of the action button
* @param successMessage - toast shown on success
* @param errorMessage - fallback toast prefix on error
* @param requiredMessage - toast shown when no value is provided
* @param onRun - async operation to run with the trimmed value
* @param renderInput - optional custom input; receives the current value and a setter
*/
export default function ToolActionCard({
  icon,
  title,
  notice,
  inputLabel,
  buttonLabel,
  successMessage,
  errorMessage,
  requiredMessage,
  onRun,
  renderInput
}: {
  icon?: ReactNode,
  title: string,
  notice: string,
  inputLabel?: string,
  buttonLabel: string,
  successMessage: string,
  errorMessage: string,
  requiredMessage: string,
  onRun: (value: string) => Promise<unknown>,
  renderInput?: (args: { value: string, setValue: (v: string) => void, disabled: boolean }) => ReactNode
}) {
  const [value, setValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  /**
  * run: validate the input then run the action
  */
  const run = () => {
    if (!value.trim()) {
      toast.error(requiredMessage);
      return;
    }

    setLoading(true);
    onRun(value.trim())
      .then(() => {
        toast.success(successMessage);
        setValue("");
      })
      .catch((err) => {
        console.error(err);
        showApiErrorToast(err, { fallbackMessage: errorMessage });
      })
      .finally(() => setLoading(false));
  };
  /***/

  return (
    <Card className="border">
      <CardBody className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          {icon}
          <Typography variant="h6">{title}</Typography>
        </div>

        <Typography variant="small" className="text-gray-600">{notice}</Typography>

        {renderInput ?
          renderInput({ value, setValue, disabled: loading })
          :
          <Input label={inputLabel || ""}
            value={value}
            labelProps={{ className: "!w-fit !h-fit bg-white px-1" }}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") run(); }} />}

        <div className="flex justify-end">
          {loading ?
            <Loading size="h-5 w-5" />
            :
            <Button color="blue" onClick={run}>{buttonLabel}</Button>}
        </div>
      </CardBody>
    </Card>
  );
}
/***/
